import { NgComponentOutlet } from '@angular/common';
import {
  Component,
  input,
  output,
  computed,
  signal,
  effect,
  OnDestroy,
  Type,
  forwardRef,
} from '@angular/core';

import { NgxSpinnerModule } from 'ngx-spinner';

import { IconType, ModalVariant } from '@shared-enums';
import { ModalConfig, VariantConfig } from '@shared-models';

import { VOneButtonComponent } from '../v-one-button/v-one-button.component';
import { VOneIconComponent } from '../v-one-icon/v-one-icon.component';

@Component({
  selector: 'v-one-modal',
  imports: [
    NgComponentOutlet,
    NgxSpinnerModule,
    forwardRef(() => VOneButtonComponent),
    forwardRef(() => VOneIconComponent),
  ],
  templateUrl: './v-one-modal.component.html',
  styleUrls: ['./v-one-modal.component.scss'],
})
export class VOneModalComponent implements OnDestroy {
  config = input<ModalConfig>({
    title: '',
    variant: ModalVariant.DIALOG,
    acceptText: 'Acción 1',
    rejectText: 'Acción 2',
    showReject: true,
    showClose: true,
  });
  visible = input<boolean>(false);

  component = input<Type<unknown>>();
  componentInputs = input<Record<string, unknown>>();

  isClosing = signal<boolean>(false);

  private windowWidth = signal<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1024,
  );

  visibleChange = output<boolean>();
  accept = output<void>();
  reject = output<void>();
  closed = output<void>();

  readonly ICON_TYPE = IconType;

  readonly modalClasses = computed(() => {
    const cfg = this.config();
    const baseClass = 'v-one-modal';
    const variantClass = `${baseClass}--${cfg.variant || ModalVariant.DIALOG}`;
    const customClass = cfg.customClass || '';

    return [baseClass, variantClass, customClass].filter(Boolean).join(' ');
  });

  readonly dialogClasses = computed(() => {
    const cfg = this.config();
    const baseClass = 'v-one-modal__dialog';
    const variantClass = `${baseClass}--${cfg.variant || ModalVariant.DIALOG}`;

    return [baseClass, variantClass].filter(Boolean).join(' ');
  });

  readonly variantConfig = computed((): VariantConfig => {
    return this.getVariantConfiguration(
      this.config().variant || ModalVariant.DIALOG,
    );
  });

  readonly hasComponent = computed(() => {
    return !!this.component();
  });

  /**
   * Header alignment. Left as null unless the modal asks for 'start', so the
   * stylesheet keeps deciding per breakpoint — an inline style would win over
   * every media query.
   */
  readonly headerAlignOverride = computed(() =>
    this.config().headerAlign === 'start' ? 'flex-start' : null,
  );

  readonly headerTextAlignOverride = computed(() =>
    this.config().headerAlign === 'start' ? 'left' : null,
  );

  /** Flattens the body's side padding so hosted content reaches the edges. */
  readonly bodyPaddingOverride = computed(() =>
    this.config().bodyPadding === 'none' ? '0' : null,
  );

  readonly hasCompactHeading = computed(() => {
    const scale = this.config().headingScale;

    return scale === 'compact' || scale === 'touch';
  });

  /** Raises the compact heading's phone sizes for a touch-scale form. */
  readonly hasTouchHeading = computed(
    () => this.config().headingScale === 'touch',
  );

  readonly hasHeaderDivider = computed(
    () => this.config().headerDivider === true,
  );

  readonly shouldShowReject = computed(() => {
    return this.config().showReject !== false;
  });

  readonly shouldShowClose = computed(() => {
    const variant = this.config().variant || ModalVariant.DIALOG;
    const hasCloseConfig = this.config().showClose !== false;

    // Don't show close button for SUCCESS, WARNING, ERROR, INFO variants
    const variantsWithoutClose = [
      ModalVariant.SUCCESS,
      ModalVariant.WARNING,
      ModalVariant.ERROR,
      ModalVariant.INFO,
    ];

    if (variantsWithoutClose.includes(variant)) {
      return false;
    }

    return hasCloseConfig;
  });

  readonly shouldShowDragHandle = computed(() => {
    return this.isMobile() && this.hasComponent() && this.shouldShowClose();
  });

  readonly shouldShowCloseButton = computed(() => {
    return this.shouldShowClose() && !this.shouldShowDragHandle();
  });

  readonly isMobile = computed(() => {
    return this.windowWidth() < 768;
  });

  readonly shouldUseBottomSheet = computed(() => {
    return this.isMobile() && this.hasComponent();
  });

  private touchStartY = 0;
  private touchCurrentY = 0;
  private isDragging = false;

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleResize);
    }

    effect(() => {
      if (this.visible()) {
        document.body.style.overflow = 'hidden';
        this.isClosing.set(false);
      } else {
        document.body.style.overflow = '';
      }
    });
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';

    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleResize);
    }
  }

  private handleResize = (): void => {
    this.windowWidth.set(window.innerWidth);
  };

  handleAccept(): void {
    this.accept.emit();
    this.handleClose();
  }

  handleReject(): void {
    this.reject.emit();
    this.handleClose();
  }

  handleClose(): void {
    if (this.isClosing()) {
      return;
    }

    this.isClosing.set(true);

    setTimeout(() => {
      this.visibleChange.emit(false);
      this.closed.emit();
    }, 300);
  }

  handleBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.handleClose();
    }
  }

  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.handleClose();
    }
  }

  onTouchStart(event: TouchEvent): void {
    if (!this.shouldUseBottomSheet()) return;

    if (event.touches.length === 1) {
      this.touchStartY = event.touches[0].clientY;
      this.isDragging = false;
    }
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.shouldUseBottomSheet()) return;

    if (event.touches.length === 1 && !this.isDragging) {
      this.touchCurrentY = event.touches[0].clientY;
      const deltaY = this.touchCurrentY - this.touchStartY;

      if (deltaY > 50) {
        const dialogElement = (event.target as HTMLElement).closest(
          '.v-one-modal__dialog',
        );

        if (dialogElement) {
          const contentElement = dialogElement.querySelector(
            '.v-one-modal__content',
          );

          const isScrolledToTop = contentElement
            ? contentElement.scrollTop === 0
            : true;

          if (isScrolledToTop) {
            this.isDragging = true;
            event.preventDefault();
            this.handleClose();
          }
        }
      }
    }
  }

  onTouchEnd(): void {
    if (!this.shouldUseBottomSheet()) return;

    this.isDragging = false;
    this.touchStartY = 0;
    this.touchCurrentY = 0;
  }

  private getVariantConfiguration(variant: ModalVariant): VariantConfig {
    const variantMap: Record<ModalVariant, VariantConfig> = {
      [ModalVariant.DIALOG]: {
        borderColor: 'var(--color-gray-400)',
        icon: '',
        iconColor: 'var(--color-gray-700)',
      },
      [ModalVariant.SUCCESS]: {
        borderColor: 'var(--color-success-600)',
        icon: 'success',
        iconColor: 'var(--color-success-600)',
      },
      [ModalVariant.WARNING]: {
        borderColor: 'var(--color-warning-600)',
        icon: 'warning',
        iconColor: 'var(--color-warning-600)',
      },
      [ModalVariant.ERROR]: {
        borderColor: 'var(--color-orange-500)',
        icon: 'warning',
        iconColor: 'var(--color-orange-500)',
      },
      [ModalVariant.INFO]: {
        borderColor: 'var(--color-info-600)',
        icon: 'info',
        iconColor: 'var(--color-info-600)',
      },
    };

    return variantMap[variant];
  }
}
